import { useState, useEffect, useCallback } from "react";
import {
  Star, Plus, Trash2, MapPin, Phone, MessageSquare,
  Loader2, Sparkles, CheckCircle2,
} from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Skeleton } from "../ui/skeleton";
import { toast } from "sonner";

export default function GoogleGMBTab({ api, onAskAgent }) {
  const [stats, setStats] = useState(null);
  const [locations, setLocations] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [selectedLoc, setSelectedLoc] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [newLoc, setNewLoc] = useState({ name: "", address: "", phone: "" });
  const [loading, setLoading] = useState(true);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [replying, setReplying] = useState(null);
  const [replyText, setReplyText] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, l] = await Promise.allSettled([api.get("/google/gmb/stats"), api.get("/google/gmb/locations")]);
      if (s.status === "fulfilled") setStats(s.value.data);
      if (l.status === "fulfilled") setLocations(l.value.data || []);
    } catch (e) { /* ignore */ }
    setLoading(false);
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const loadReviews = useCallback(async (loc) => {
    setSelectedLoc(loc);
    setReviewLoading(true);
    try {
      const r = await api.get(`/google/gmb/locations/${loc.location_id}/reviews`);
      setReviews(r.data || []);
    } catch (e) { /* ignore */ }
    setReviewLoading(false);
  }, [api]);

  const handleCreate = async () => {
    if (!newLoc.name || !newLoc.address) { toast.error("Nom et adresse requis"); return; }
    try {
      await api.post("/google/gmb/locations", newLoc);
      toast.success("Fiche ajoutee");
      setShowNew(false);
      setNewLoc({ name: "", address: "", phone: "" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const handleDelete = async (loc_id) => {
    if (!window.confirm("Supprimer cette fiche et tous ses avis ?")) return;
    await api.delete(`/google/gmb/locations/${loc_id}`);
    if (selectedLoc?.location_id === loc_id) setSelectedLoc(null);
    load();
  };

  const submitReply = async (review_id) => {
    const text = replyText[review_id] || "";
    if (!text.trim()) return;
    try {
      await api.post(`/google/gmb/reviews/${review_id}/reply`, { text });
      toast.success("Reponse publiee (mock — Phase 2 push reel)");
      setReplyText({ ...replyText, [review_id]: "" });
      setReplying(null);
      if (selectedLoc) loadReviews(selectedLoc);
    } catch (e) {
      toast.error("Erreur");
    }
  };

  const askAgentForReply = (review) => {
    onAskAgent({
      domain: "gmb",
      review: `Auteur: ${review.author_name}\nNote: ${review.rating}/5\nTexte: ${review.text}`,
      action: "reply_review",
    });
  };

  const renderStars = (n) => "★".repeat(n) + "☆".repeat(5 - n);

  return (
    <div className="space-y-5" data-testid="google-gmb-tab">
      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: "Fiches", value: stats.total_locations, color: "text-white" },
            { label: "Avis total", value: stats.total_reviews, color: "text-white" },
            { label: "Sans reponse", value: stats.unanswered_reviews, color: "text-amber-400" },
            { label: "Note moyenne", value: stats.avg_rating, color: "text-[#f7c935]", suffix: "/5" },
          ].map((s, i) => (
            <div key={i} className="bg-[#14101c] border border-white/5 rounded-xl p-4" data-testid={`gmb-stat-${i}`}>
              <div className="text-[10px] uppercase text-slate-500 font-bold tracking-wider mb-1">{s.label}</div>
              <div className={`text-2xl font-black ${s.color}`}>{s.value}{s.suffix || ""}</div>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-bold text-white">Fiches Google Business</h3>
          <p className="text-xs text-slate-500 mt-0.5">Gerez vos fiches GMB et repondez aux avis avec l'aide de l'agent IA.</p>
        </div>
        <Button onClick={() => setShowNew(!showNew)} className="bg-[#f7c935] text-[#0b0811] hover:bg-[#e6b825] font-bold" data-testid="gmb-add-loc-btn">
          <Plus className="w-4 h-4 mr-1" /> Ajouter une fiche
        </Button>
      </div>

      {showNew && (
        <div className="bg-[#14101c] border border-[#f7c935]/30 rounded-xl p-4 space-y-3" data-testid="gmb-new-form">
          <Input placeholder="Nom de l'etablissement" value={newLoc.name} onChange={e => setNewLoc({ ...newLoc, name: e.target.value })} className="bg-[#0b0811] border-white/10 text-white" data-testid="gmb-input-name" />
          <Input placeholder="Adresse complete" value={newLoc.address} onChange={e => setNewLoc({ ...newLoc, address: e.target.value })} className="bg-[#0b0811] border-white/10 text-white" />
          <Input placeholder="Telephone" value={newLoc.phone} onChange={e => setNewLoc({ ...newLoc, phone: e.target.value })} className="bg-[#0b0811] border-white/10 text-white" />
          <div className="flex gap-2">
            <Button onClick={handleCreate} className="bg-[#f7c935] text-[#0b0811] hover:bg-[#e6b825] font-bold" data-testid="gmb-confirm-btn">Ajouter</Button>
            <Button onClick={() => setShowNew(false)} variant="outline" className="border-white/10 text-white">Annuler</Button>
          </div>
          <div className="text-[10px] text-amber-300/80 bg-amber-500/5 border border-amber-500/15 rounded p-2">
            <strong>Phase 1 :</strong> donnees demonstration. Phase 2 : connexion OAuth Google reelle pour synchroniser vos vraies fiches GMB.
          </div>
        </div>
      )}

      {loading ? (
        <div className="grid gap-3">{[...Array(2)].map((_, i) => <Skeleton key={i} className="h-20 rounded-xl" />)}</div>
      ) : locations.length === 0 ? (
        <div className="text-center py-12 bg-[#14101c] border border-white/5 rounded-xl">
          <Star className="w-10 h-10 text-slate-600 mx-auto mb-3" />
          <p className="text-sm text-slate-400">Aucune fiche enregistree.</p>
        </div>
      ) : (
        <div className="grid gap-3">
          {locations.map(loc => (
            <div
              key={loc.location_id}
              className={`bg-[#14101c] border rounded-xl p-4 transition-all cursor-pointer ${selectedLoc?.location_id === loc.location_id ? "border-[#f7c935]/50" : "border-white/5 hover:border-white/10"}`}
              onClick={() => loadReviews(loc)}
              data-testid={`gmb-loc-${loc.location_id}`}
            >
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="w-10 h-10 rounded-lg bg-[#0b0811] flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-[#EA4335]" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-bold text-white">{loc.name}</div>
                    <div className="text-xs text-slate-500 truncate">{loc.address}</div>
                    {loc.phone && <div className="text-xs text-slate-500 flex items-center gap-1 mt-0.5"><Phone className="w-3 h-3" /> {loc.phone}</div>}
                  </div>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <div className="text-center">
                    <div className="text-[#f7c935] text-sm">{renderStars(Math.round(loc.avg_rating))}</div>
                    <div className="text-[10px] text-slate-500">{loc.avg_rating} ({loc.reviews_count})</div>
                  </div>
                  <Button onClick={(e) => { e.stopPropagation(); handleDelete(loc.location_id); }} size="sm" variant="ghost" className="text-red-400 hover:bg-red-500/10">
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Reviews panel */}
      {selectedLoc && (
        <div className="bg-[#14101c] border border-[#f7c935]/30 rounded-xl p-4" data-testid="gmb-reviews-panel">
          <h4 className="text-sm font-bold text-white mb-3 flex items-center gap-2"><MessageSquare className="w-4 h-4 text-[#f7c935]" /> Avis pour {selectedLoc.name}</h4>
          {reviewLoading ? (
            <div className="space-y-2">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-16 rounded-lg" />)}</div>
          ) : reviews.length === 0 ? (
            <p className="text-xs text-slate-500">Aucun avis.</p>
          ) : (
            <div className="space-y-2 max-h-[500px] overflow-y-auto">
              {reviews.map(rv => (
                <div key={rv.review_id} className="bg-[#0b0811] border border-white/5 rounded-lg p-3" data-testid={`gmb-review-${rv.review_id}`}>
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <div>
                      <div className="text-xs font-bold text-white">{rv.author_name}</div>
                      <div className="text-[#f7c935] text-xs">{renderStars(rv.rating)}</div>
                    </div>
                    <div className="text-[10px] text-slate-500">{(rv.created_at || "").slice(0, 10)}</div>
                  </div>
                  <p className="text-xs text-slate-300 my-2">{rv.text}</p>
                  {rv.reply ? (
                    <div className="bg-emerald-500/5 border-l-2 border-emerald-500/40 pl-2 mt-2">
                      <div className="text-[9px] uppercase font-bold text-emerald-400 tracking-wider">Reponse publiee</div>
                      <p className="text-xs text-slate-300 mt-1">{rv.reply}</p>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 mt-2">
                      {replying === rv.review_id ? (
                        <>
                          <Input placeholder="Votre reponse..." value={replyText[rv.review_id] || ""} onChange={e => setReplyText({ ...replyText, [rv.review_id]: e.target.value })} className="bg-[#14101c] border-white/10 text-white text-xs h-8 flex-1" data-testid={`gmb-reply-input-${rv.review_id}`} />
                          <Button onClick={() => submitReply(rv.review_id)} size="sm" className="bg-[#f7c935] text-[#0b0811] hover:bg-[#e6b825] font-bold h-8" data-testid={`gmb-reply-send-${rv.review_id}`}>Envoyer</Button>
                          <Button onClick={() => setReplying(null)} size="sm" variant="ghost" className="text-slate-400 h-8">Annuler</Button>
                        </>
                      ) : (
                        <>
                          <Button onClick={() => setReplying(rv.review_id)} size="sm" variant="outline" className="border-white/10 text-white text-xs h-7" data-testid={`gmb-reply-btn-${rv.review_id}`}>Repondre</Button>
                          <Button onClick={() => askAgentForReply(rv)} size="sm" className="bg-gradient-to-r from-[#4285F4] to-[#EA4335] hover:opacity-90 text-white text-xs h-7" data-testid={`gmb-ai-reply-${rv.review_id}`}>
                            <Sparkles className="w-3 h-3 mr-1" /> Reponse IA
                          </Button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
